<template>
  <div class="import-source-ui">
    <ImportSourceSkeleton v-if="isLoading" />
    <VFallbackText v-else-if="hasError">
      Sorry something went wrong during fetching sources
    </VFallbackText>
    <VDropdown
      v-else-if="sourcesList.length"
      v-model="proxyModelValue"
      label="Select source"
      :options="sourcesListOptions"
      :disabled="isDisabled"
    />
    <VFallbackText v-else>No sources found</VFallbackText>
    <div class="import-source-ui__create-btn">
      <VBtn class="slim flat full-width" :disabled="isDisabled" @click="showCreateModal">
        + Create new
      </VBtn>
    </div>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { WritableComputedRef } from 'vue';
  import type {
    ICompanySourceModel,
    TCompanySource,
    TCompanySourceListResponse,
  } from '~/shared/source/types';
  import type { IVDropdownOption } from '~/misc/v-dropdown/types';

  // Components
  import ImportSourceSkeleton from '~/shared/import/source/skeleton/ImportSourceSkeleton.vue';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VDropdown from '~/misc/v-dropdown';
  import VBtn from '~/misc/v-btn';

  const CreateModal = defineAsyncComponent(
    () => import('~/modules/manager/company/sources/create-modal/SourceCreateModal.vue')
  );

  const props = defineProps({
    resolver: {
      type: Object as PropType<ICompanySourceModel>,
      required: true,
    },
    modelValue: {
      type: Number as PropType<number | null>,
      default: null,
    },
    companyId: {
      type: Number,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits<{
    (e: 'select', data: TCompanySource | null): void;
  }>();

  const { show: showModal } = useModal();

  const sourcesList: Ref<TCompanySource[]> = ref<TCompanySource[]>([]);
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);

  const proxyModelValue: WritableComputedRef<number | null> = computed({
    get(): number | null {
      return props.modelValue;
    },
    set(id: number | null): void {
      emit('select', sourcesList.value.find((source: TCompanySource) => source.id === id) ?? null);
    },
  });

  const sourcesListOptions: ComputedRef<IVDropdownOption<string, number>[]> = computed<
    IVDropdownOption<string, number>[]
  >(() => sourcesList.value.map((item: TCompanySource) => ({ label: item.name, value: item.id })));

  const setSourcesList = (list: TCompanySource[]) => {
    sourcesList.value = list;
  };

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const fetchSources = async () => {
    try {
      setIsLoading(true);

      const list: TCompanySourceListResponse = await props.resolver.list(props.companyId);

      setSourcesList(list.data);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const showCreateModal = () => {
    showModal(CreateModal, {
      model: props.resolver,
      companyId: props.companyId,
      createdCallback: async (source: TCompanySource) => {
        await fetchSources();

        emit('select', source);
      },
    });
  };

  // The initial fetchSources() call is still needed because ImportSource can
  // be mounted/unmounted via v-if within an already-active tab
  fetchSources();

  onActivated(() => fetchSources());
</script>
